import {
  first,
  last,
  upperFirst,
  lowerFirst,
  join,
  upperCase,
  every,
  toLower,
  toUpper,
  reverse,
  groupBy,
  cloneDeep,
} from 'lodash';

import {
  calculateTime,
  hasPunctuation,
  isLetterUpperCase,
  isWordUpperCase,
  removePunctuation,
} from '../functions';

// ***************************************************************************************
// addCommonChar() appelée par l'application lors de l'appui sur une touche du clavier
// correspondant à un nouveau caractère à insérer dans le script
// ***************************************************************************************
export function addCommonChar(char) {

  if(!this.subtitle){
    return;
  }

  if (this.underSelection) {
    this.addNewActionForUndo('addChar');

    if (this.lettersToSelect.length) {
      const gpWords = groupBy(
        this.lettersToSelect,
        (x) => `${x.indexLine}_${x.indexWord}`
      );
      const keys = Object.keys(gpWords);
      const wordsSelected = this.subtitle.lines
        .flatMap((x) => x.words)
        .filter((x) => keys.includes(`${x.indexLine}_${x.index}`));

      // Si c'est un seul mot
      if (keys.length === 1) {
        const startLetter = first(this.lettersToSelect);
        const endLetter = last(this.lettersToSelect);

        const word =
          this.subtitle.lines[startLetter.indexLine].words[
            startLetter.indexWord
          ];

        word.text =
          word.text.substring(0, startLetter.index) +
          char +
          word.text.substring(endLetter.index + 1);

        // Placer le curseur sur le mot
        this.currentWordIndex = word.index;
        this.currentLineIndex = word.indexLine;
        this.currentLetterIndex = startLetter.indexLetter + 1;
      } else {
        const wordsToDelete = [];
        const wordsToRegroup = [];

        for (const word of wordsSelected) {
          const lettersToDelete = this.lettersToSelect.filter(
            (x) => x.indexWord === word.index && x.indexLine === word.indexLine
          );

          if (lettersToDelete.length) {
            const startLetter = first(lettersToDelete);
            const endLetter = last(lettersToDelete);

            word.text =
              word.text.substring(0, startLetter.index) +
              word.text.substring(endLetter.index + 1);

            if (word.text.length === 0) {
              wordsToDelete.push(word);
            } else {
              wordsToRegroup.push(word);
            }
          }
        }

        // si tous les mots ont été sélectionnés et supprimés
        if (wordsToDelete.length && !wordsToRegroup.length) {
          const firstWord = cloneDeep(first(wordsToDelete));
          const lastWord = cloneDeep(last(wordsToDelete));

          for (const word of reverse(wordsToDelete)) {
            this.subtitle.lines[word.indexLine].words.splice(word.index, 1);
          }

          const newWord = {
            ...firstWord,
            text: char,
            out: lastWord.out,
            letters: [],
          };

          this.subtitle.lines[newWord.indexLine].words.splice(
            newWord.index,
            0,
            newWord
          );

          const startLetter = first(this.lettersToSelect);

          // On place le curseur sur le mot
          this.currentWordIndex = newWord.index;
          this.currentLineIndex = newWord.indexLine;
          this.currentLetterIndex = startLetter.indexLetter + 1;

          this.lettersToSelect = [];
          this.underSelection = false;

          this.onChange();

          return;
        }

        const newWord = cloneDeep(first(wordsToRegroup));

        newWord.text += char;

        if (wordsToRegroup.length > 1) {
          const lastWord = last(wordsToRegroup);

          newWord.out = lastWord.out;
          newWord.text += lastWord.text;
        }

        for (const word of reverse(wordsSelected)) {
          this.subtitle.lines[word.indexLine].words.splice(word.index, 1);
        }

        this.subtitle.lines[newWord.indexLine].words.splice(
          newWord.index,
          0,
          newWord
        );

        const startLetter = first(this.lettersToSelect);

        this.currentWordIndex = newWord.index;
        this.currentLineIndex = newWord.indexLine;
        this.currentLetterIndex = startLetter.indexLetter + 1;
      }

      this.lettersToSelect = [];
      this.underSelection = false;

      this.onChange();

      return;
    }

    if (this.wordsToSelect.length) {
      const firstWord = first(this.wordsToSelect);
      const lastWord = last(this.wordsToSelect);

      const newWord = {
        ...firstWord,
        text: char,
        out: lastWord.out,
        letters: [],
      };

      // on supprime tous les mots sélectionnées
      const wordsReverse = reverse(this.wordsToSelect);
      wordsReverse.forEach((itemToDelete) => {
        this.subtitle.lines[itemToDelete.indexLine].words.splice(
          itemToDelete.index,
          1
        );
      });

      this.subtitle.lines[newWord.indexLine].words.splice(
        newWord.index,
        0,
        newWord
      );

      this.currentLineIndex = newWord.indexLine;
      this.currentWordIndex = newWord.index;

      if (firstWord.letters.length)
      {      
        this.currentLetterIndex = first(firstWord.letters).indexLetter + 1;
      } 

      this.wordsToSelect = [];
      this.onChange();
    }

    this.underSelection = false;

    return;
  }

  const currentLine = this.subtitle.lines[this.currentLineIndex];
  const currentWord = currentLine.words[this.currentWordIndex];
  const currentLetter =
    currentWord && currentWord.letters
      ? currentWord.letters.find(
          (x) => x.indexLetter === this.currentLetterIndex
        )
      : null;

  const { doubleHeight, fontColor, fontItalic, verticalPos } =
    this.props.subtitleParameters;

  const vPos = currentLine.verticalPos ?? verticalPos;
  const nextPos = doubleHeight ? 2 : 1;

  // On ne doit pas dépasser la longueur maximale par ligne
  const nbChars = this.getSentence(currentLine).length;
  const maxLength = this.getMaxLength(currentLine);

  if (nbChars >= maxLength) {
    // ca va à la ligne suivante
    const nextLine = this.subtitle.lines.find(
      (x) => x.verticalPos === vPos + nextPos
    );

    // prendre le dernier mot pour le mettre à la ligne
    const lastWord = last(currentLine.words);

    if (nextLine) {
      const newWordsLine = [lastWord, ...nextLine.words];
      const newSentenceLength = newWordsLine
        .map((x) => x.text)
        .join(' ').length;

      if (newSentenceLength >= this.getMaxLength(nextLine)) {
        return;
      }

      nextLine.words.unshift(lastWord);
    } else {
      const newLine = this.createNewLine(this.subtitle, currentLine);
      if (!newLine) {
        return;
      }

      newLine.words.push(lastWord);
      this.subtitle.lines.push(newLine);
    }

    currentLine.words.splice(lastWord.index, 1);

    // si le mot courant est le dernier de la ligne
    if (lastWord.index === currentWord.index) {
      this.currentLetterIndex = currentLetter ? currentLetter.index : 0;
      this.currentWordIndex = 0;
      this.currentLineIndex++;
    }

    this.onChange();
    this.addCommonChar(char);
    return;
  }

  // si le char est un espace
  if (char === ' ') {
    this.addSpace();
    return;
  }

  if (this.hasEmptyWord) {
    this.hasEmptyWord = false;
  }

  // Faire l'historique
  this.addNewActionForUndo('addChar');

  if (!currentLine.words.length) {
    let inTime = 0;
    let outTime = 0;

    if (this.currentLineIndex === 0) {
      inTime = this.subtitle.in;
    } else {
      const prevLine = this.subtitle.lines[this.currentLineIndex - 1];

      if (prevLine && prevLine.words.length) {
        inTime = last(prevLine.words).out;
      } else {
        inTime = this.subtitle.in;
      }
    }

    const nextLine = this.subtitle.lines[this.currentLineIndex + 1];
    if (nextLine && nextLine.words.length) {
      outTime = first(nextLine.words).out;
    } else {
      outTime = this.subtitle.out;
    }

    const newWord = {
      color: this.subtitle.color ?? fontColor,
      italic: fontItalic,
      text: char,
      in: inTime,
      out: outTime,
    };

    currentLine.words.push(newWord);

    this.currentWordIndex = 0;
    this.currentLetterIndex = 1;

    this.onChange();

    return;
  }

  const currentText = currentWord.text;

  if (currentText === '') {
    currentWord.text = char;
  } else {
    const currentIndex = currentLetter
      ? currentLetter.index
      : currentText.length;
    const startText = currentText.substring(0, currentIndex);
    const endText = currentText.substring(currentIndex);

    currentWord.text = startText + char + endText;
  }

  // si la derniere lettre du mot précédent a un point
  const prevWord = this.getPrevWord();
  if (
    prevWord &&
    (prevWord.text.endsWith('.') ||
      prevWord.text.endsWith('!') ||
      prevWord.text.endsWith('?'))
  ) {
    if (!("upper" in currentWord))
    {
      currentWord.text = upperFirst(currentWord.text.toLowerCase());
      currentWord.upper = true;
    }
  }

  // si le char est un point alors, mettre la lettre suivante au majuscule
  /*
  if (char === '.' || char === '?' || char === '!') {
    // si le dernier élément est un espace
    const lastLetter = currentWord.letters[currentWord.letters.length - 1];

    if (
      lastLetter &&
      currentLetter &&
      lastLetter.index === currentLetter.index
    ) {
      const nextWord = this.getNextWord();
      if (nextWord && !isLetterUpperCase(nextWord.text[0])) {
        nextWord.text = upperFirst(nextWord.text);
      }
    }
    else {
      currentWord.text =
        currentWord.text.substring(0, currentLetter.index) + 
        char +
        upperFirst(currentWord.text.substring(currentLetter.index + 1));
    }
  }
  */
  this.currentLetterIndex++;

  this.onChange();
}

// ***************************************************************************************
// addCommonHebrewOrArabicChar()
// on ajoute les caractères en hébreu et en arabe de droite à gauche
// ***************************************************************************************
export function addCommonHebrewOrArabicChar(char) {
  // On ne doit pas dépasser la longueur maximale par ligne
  const nbChars = this.getCurrentSentence().length;
  const maxLength = this.getMaxLength(this.currentLine);

  if (nbChars >= maxLength) {
    return;
  }

  // si le char est un espace
  if (char === ' ') {
    this.addSpace();
    return;
  }

  // Faire l'historique
  this.addNewActionForUndo('addChar');

  if (!this.currentLine.words.length) {
    const { fontColor, fontItalic } = this.props.subtitleParameters;

    let inTime = 0;
    let outTime = 0;

    if (this.currentLineIndex === 0) {
      inTime = this.subtitle.in;
    } else {
      const prevLine = this.subtitle.lines[this.currentLineIndex - 1];

      if (prevLine && prevLine.words.length) {
        inTime = last(prevLine.words).out;
      } else {
        inTime = this.subtitle.in;
      }
    }

    const nextLine = this.subtitle.lines[this.currentLineIndex + 1];
    if (nextLine && nextLine.words.length) {
      outTime = first(nextLine.words).out;
    } else {
      outTime = this.subtitle.out;
    }

    const newWord = {
      color: this.subtitle.color ?? fontColor,
      italic: fontItalic,
      text: char,
      in: inTime,
      out: outTime,
      rtl: true,
    };

    this.currentLine.words.push(newWord);

    this.currentWordIndex = 0;
    this.currentLetterIndex = 0;

    this.onChange();

    return;
  }

  const currentText = this.currentWord.text;

  if (currentText === '') {
    this.currentWord.text = char;
  } else {
    if (currentText.length === 1) {
      this.currentWord.text = currentText + char;
    } else {
      const currentIndex = this.currentLetter ? this.currentLetter.index : 0;

      const startIndex = currentText.length - currentIndex;
      this.currentWord.text =
        currentText.substring(0, startIndex) +
        char +
        currentText.substring(startIndex);
    }
  }

  this.currentWord.rtl = true;

  this.onChange();
}

// ***************************************************************************************
// addSpace permet d'ajouter un espace (soit en couper le mot en deux soit en créant un nouveau mot)
// ***************************************************************************************
export function addSpace() {
  const currentLine = this.subtitle.lines[this.currentLineIndex];
  const currentWord = currentLine.words[this.currentWordIndex];
  if (!currentWord) return;
  if (!currentWord.letters) return;
  const currentIndex = currentWord.letters
    ? currentWord.letters.findIndex(
        (l) => l.indexLetter === this.currentLetterIndex
      )
    : 0;

  if (currentWord.rtl) {
    this.addNewActionForUndo('addSpace');

    // on créé un nouveau mot
    if (currentIndex === 0) {
      const newWord = {
        ...currentWord,
        text: '',
        letters: [],
      };

      const prevWord = currentLine.words[this.currentWordIndex - 1];
      if (prevWord) {
        newWord.in = prevWord.out;
      } else {
        newWord.in = this.subtitle.in;
      }

      newWord.out = currentWord.in;

      currentLine.words.splice(this.currentWordIndex, 0, newWord);
      this.hasEmptyWord = true;

      this.onChange();

      return;
    }

    const startIndex = currentWord.text.length - currentIndex;

    const textWord1 = currentWord.text.substring(startIndex);
    const textWord2 = currentWord.text.substring(0, startIndex);

    const word1 = {
      ...currentWord,
      text: textWord1,
      letters: [],
    };

    const word2 = {
      ...currentWord,
      text: textWord2,
      letters: [],
    };

    currentLine.words.splice(this.currentWordIndex, 1, ...[word1, word2]);
    this.onChange();
    return;
  }

  const textWord1 = currentWord.text.substring(0, currentIndex);
  const textWord2 = currentWord.text.substring(currentIndex);

  if (textWord1.length === 0) {
    return;
  }

  this.addNewActionForUndo('addSpace');
  this.hasEmptyWord = true;

  // Si le mot est vide
  if (textWord2.length === 0) {
    const newWord = {
      ...currentWord,
      text: '',
      letters: [],
    };

    const nextWord = currentLine.words[this.currentWordIndex + 1];
    if (nextWord) {
      newWord.out = nextWord.in;
    } else {
      newWord.out = currentWord.out;
    }

    currentLine.words.splice(this.currentWordIndex + 1, 0, newWord);
  } else {
    // Sinon on split le mot en deux
    const inOutTime = calculateTime(
      currentWord.in,
      currentWord.out,
      currentWord.text,
      textWord1
    );

    const word1 = {
      ...currentWord,
      text: textWord1,
      in: currentWord.in,
      out: inOutTime,
      letters: [],
    };

    const word2 = {
      ...currentWord,
      text: textWord2,
      in: inOutTime,
      out: currentWord.out,
      letters: [],
    };

    // si le caractère précédent est un point
    if (
      textWord1.endsWith('.') ||
      textWord1.endsWith('!') ||
      textWord1.endsWith('?')
    ) {
      word2.text = upperFirst(word2.text.toLowerCase());
    }

    currentLine.words[this.currentWordIndex] = word1;
    currentLine.words.splice(this.currentWordIndex + 1, 0, word2);
  }

  this.currentWordIndex++;
  this.currentLetterIndex++;

  this.onChange();
}

// ***************************************************************************************
// addHyphen permet d'ajouter le tiret
// si le caractère est un tiret, alors on met un point dans le mot précédent
// on met au majuscule le mot suivant
// ***************************************************************************************
export function addHyphen() {

  if (!this.currentWord) {
    if (!this.currentLine.words.length) {
      this.addNewActionForUndo('addPeriod');
      this.createNewWord(this.subtitle, "- ", this.currentLineIndex);
      this.onChange();

      if (this.currentLetterIndex === 0){
        this.currentLetterIndex += 2;
      }
    } 
      
    return;
  }

  const { text } = this.currentWord;
  if (text.includes('-')) {
    return;
  }

  this.addNewActionForUndo('addHyphen');

  if (!text) {
    this.currentWord.text = `- `;
    this.currentLetterIndex += 2;
    this.onChange();
    return;
  }

  this.currentWord.text = `- ${upperFirst(text)}`;
  this.currentLetterIndex += 2;

  const currentLine = this.subtitle.lines[this.currentLineIndex];

  // On met le point dans le mot précédent
  const prevWord = currentLine.words[this.currentWordIndex - 1];
  if (prevWord) {
    const lastLetter = prevWord.text[prevWord.text.length - 1];
    if (lastLetter !== '.') {
      prevWord.text += '.';
      this.currentLetterIndex++;
    }
  }

  this.onChange();
}

// ***************************************************************************************
// onReturnToLineWithHypen permet de mettre à la ligne avec un tiret
// ***************************************************************************************
export function onReturnToLineWithHypen() {
  const currentLine = this.subtitle.lines[this.currentLineIndex];
  const currentWord = currentLine.words[this.currentWordIndex];

  const { text, index } = currentWord;

  if (text === '' || text.startsWith('-')) {
    return;
  }

  this.addNewActionForUndo('onReturnToLineWithHypen');

  if (this.currentWordIndex !== 0) {
    // on met les mots à la ligne
    const nextWords = currentLine.words.filter((x) => x.index >= index);
    this.setWordsToNewLine(index, nextWords);

    // On met le point dans le mot précédent
    const prevWord = currentLine.words[this.currentWordIndex - 1];
    if (prevWord) {
      const lastLetter = prevWord.text[prevWord.text.length - 1];
      if (lastLetter !== '.') {
        prevWord.text += '.';
      }
    }

    this.currentLineIndex++;
    this.currentWordIndex = 0;
    this.currentLetterIndex = 1;
  }

  this.currentWord.text = `- ${upperFirst(text)}`;

  this.onChange();
}

// ***************************************************************************************
// addPeriod permet d'ajouter un point à la fin du mot
// si newline à true, alors je le mets à la ligne
// ***************************************************************************************
export function addPeriod(newline) {

  if (!this.currentWord) {
    
    if (!this.currentLine.words.length) {
      this.addNewActionForUndo('addPeriod');
      this.createNewWord(this.subtitle, ".");
      this.onChange();

      if (this.currentLetterIndex === 0){
        this.currentLetterIndex++;
      }
    } 
      
    return;
    
  }

  const { text, index } = this.currentWord;

  if (text.includes('.')) {
    return;
  }

  this.addNewActionForUndo('addPeriod');

  this.currentWord.text += '.';

  // On met au majuscule le prochain mot
  const nextWord = this.getNextWord();
  if (nextWord) {
    nextWord.text = upperFirst(nextWord.text);
  }

  if (newline) {
    const nextWords = this.getNextWordsOfLine(index + 1);
    this.setWordsToNewLine(index + 1, nextWords);
  }

  this.onChange();
}

// ***************************************************************************************
// removeUnsecableSpace permet de supprimer l'espace insécable
// ***************************************************************************************
export function removeUnsecableSpace() {
  if (!this.currentWord) {
    return;
  }

  const { index, text } = this.currentWord;

  if (text.includes(String.fromCharCode(0xa0)) || text.includes(' ')) {
    let texts = text.split(String.fromCharCode(0xa0));

    if (texts.length === 1) {
      texts = text.split(' ');
    }

    // On historise
    this.addNewActionForUndo('removeUnsecableSpace');

    const newWords = this.createWords(texts);

    this.currentLine.words.splice(index, 1, ...newWords);

    this.onChange();
  }
}

// ***************************************************************************************
// groupWordsByUnsecableSpace permet de regrouper les mots dans un espace insécable
// ***************************************************************************************
export function groupWordsByUnsecableSpace(next) {
  if (!this.underSelection) {
    this.groupWordByChar(
      'groupWordsByUnsecableSpace',
      String.fromCharCode(0xa0),
      next
    );
    return;
  }

  const firstWord = first(this.wordsToSelect);
  const lastWord = last(this.wordsToSelect);

  // On ne insécable pas les mots quand ceux-ci ne sont pas sur la même ligne
  if (firstWord.indexLine !== lastWord.indexLine) {
    return;
  }

  this.addNewActionForUndo('groupWordsByUnsecableSpace');

  const sentence = join(
    this.wordsToSelect.map((x) => x.text),
    String.fromCharCode(0xa0)
  );

  const newWord = {
    ...firstWord,
    text: sentence,
    out: lastWord.out,
    letters: [],
  };

  this.currentLine.words.splice(
    firstWord.index,
    this.wordsToSelect.length,
    newWord
  );

  this.currentWordIndex = firstWord.index;

  this.underSelection = false;

  this.onChange();
}

// ***************************************************************************************
// groupWordsWithoutSpace permet de regrouper les mots sans espace
// ***************************************************************************************
export function groupWordsWithoutSpace() {
  if (!this.underSelection) {
    this.groupWordByChar('groupWordsWithoutSpace', '', true);
    return;
  }

  const firstWord = first(this.wordsToSelect);
  const lastWord = last(this.wordsToSelect);

  // On ne insécable pas les mots quand ceux-ci ne sont pas sur la même ligne
  if (firstWord.indexLine !== lastWord.indexLine) {
    return;
  }

  this.addNewActionForUndo('groupWordsWithoutSpace');

  const sentence = join(
    this.wordsToSelect.map((x) => x.text),
    ''
  );

  const newWord = {
    ...firstWord,
    text: sentence,
    out: lastWord.out,
    letters: [],
  };

  this.currentLine.words.splice(
    firstWord.index,
    this.wordsToSelect.length,
    newWord
  );

  this.currentWordIndex = firstWord.index;

  this.underSelection = false;

  this.onChange();
}

// ***************************************************************************************
// onConversionToInitial permet de convertir le mot en un initial avec ou son espace
// ***************************************************************************************
export function onConversionToInitial(space) {
  if (!this.currentWord.text.length || this.currentWord.text === ' ') {
    return;
  }

  this.addNewActionForUndo('onConversionToInitial');

  this.currentWord.text = upperCase(this.currentWord.text[0]) + '.';

  if (!space) {
    const nextWord = this.getNextWord(false);
    if (nextWord) {
      this.currentWord.out = nextWord.out;
      this.currentWord.text += nextWord.text;

      this.currentLine.words.splice(nextWord.index, 1);
    }
  }

  this.onChange();
}

// ***************************************************************************************
// onUpperOrLowerCase permet de mettre en majuscule ou en minuscule
// ***************************************************************************************
export function onUpperOrLowerCase(key) {
  if (!this.currentWord){
    return;
  }


  const { text } = this.currentWord;

  this.addNewActionForUndo('onUpperOrLowerCase');

  // Si le texte contient un tiret comme le mot "deux-roues"
  // alors on doit faire en sorte de transformer le mot en "Deux-Roues" ou "deux-roues"
  if (key === 'UPPERCASEORLOWERCASEWITHDASH') {
    if (!text.includes('-')) {
      this.cancelUndo();
      return;
    }

    const indexDash = text.indexOf('-');

    const firstPart = text.substring(0, indexDash);
    const secondPart = text.substring(indexDash + 1);

    const isUpperCase = isLetterUpperCase(secondPart[0]);

    this.currentWord.text =
      (isUpperCase ? lowerFirst(firstPart) : upperFirst(firstPart)) +
      '-' +
      (isUpperCase ? lowerFirst(secondPart) : upperFirst(secondPart));

    this.onChange();
    return;
  }

  // Transformer le mot en majuscule
  if (key === 'UPPERCASEONWORD') {
    if (isWordUpperCase(text)) {
      this.cancelUndo();
      return;
    }

    this.currentWord.text = text.toUpperCase();

    this.onChange();
    return;
  }

  // Transformer le mot en minuscule
  if (key === 'LOWERCASEONWORD') {
    if (text === text.toLowerCase()) {
      this.cancelUndo();
      return;
    }

    this.currentWord.text = text.toLowerCase();

    this.onChange();
    return;
  }

  // Transformer le sous-titre entière en majuscule ou en minuscule
  if (key === 'UPPERCASEORLOWERCASEONSENTENCE') {
    const words = this.getWords();
    const isAllWordsUpperCase = every(words, (x) => isWordUpperCase(x.text));

    // Si tout le sous-titre est déjà au majuscule, le mettre tout au minuscule
    this.subtitle.lines.forEach((line) => {
      line.words.forEach(
        (word) =>
          (word.text = isAllWordsUpperCase
            ? word.text.toLowerCase()
            : word.text.toUpperCase())
      );
    });

    this.onChange();
    return;
  }

  if (key === 'UPPERCASEORLOWERCASEWITHAPOSTROPHEORPERIOD') {
    const indexOfLetter = this.currentWord.letters.findIndex(
      (x) => x.letter === String.fromCharCode(39) || x.letter === '.'
    );

    if (indexOfLetter === -1 || indexOfLetter === text.length - 1) {
      this.cancelUndo();
      return;
    }

    const letterIndexToUpperOrLower = indexOfLetter + 1;

    this.currentWord.text =
      text.substring(0, letterIndexToUpperOrLower) +
      (isLetterUpperCase(text[letterIndexToUpperOrLower])
        ? toLower(
            text.substring(
              letterIndexToUpperOrLower,
              letterIndexToUpperOrLower + 1
            )
          )
        : toUpper(
            text.substring(
              letterIndexToUpperOrLower,
              letterIndexToUpperOrLower + 1
            )
          )) +
      text.substring(letterIndexToUpperOrLower + 1);

    this.onChange();

    return;
  }

  // Mettre en majuscule ou en minuscule la première ou la deuxième lettre
  const firstLetter = key === 'UPPERCASEORLOWERCASEONFIRSTLETTER';
  if (!firstLetter && text.length < 2) {
    this.cancelUndo();
    return;
  }

  if (firstLetter) {
    this.currentWord.text = isLetterUpperCase(text[0])
      ? lowerFirst(text)
      : upperFirst(text);
  } else {
    this.currentWord.text =
      text.substring(0, 1) +
      (isLetterUpperCase(text[1])
        ? toLower(text.substring(1, 2))
        : toUpper(text.substring(1, 2))) +
      text.substring(2);
  }

  this.onChange();
}

// ***************************************************************************************
// addQuotes permet d'ajouter des guillemets
// ***************************************************************************************
export function addQuotes() {

  if (!this.currentWord) {
    
    if (!this.currentLine.words.length) {
      this.addNewActionForUndo('addPeriod');
      this.createNewWord(this.subtitle, String.fromCharCode(34));
      this.onChange();

      if (this.currentLetterIndex === 0){
        this.currentLetterIndex++;
      }
    } 
      
    return;
    
  }

  let { text } = this.currentWord;

  if (this.underSelection) {
    this.addNewActionForUndo('addQuotes');

    const firstWord = first(this.wordsToSelect);
    const lastWord = last(this.wordsToSelect);

    firstWord.text = String.fromCharCode(34) + firstWord.text;

    let lastText = lastWord.text;
    let punctuation = '';
    if (hasPunctuation(lastText)) {
      lastText = removePunctuation(lastText);
      punctuation = lastWord.text.substring(lastText.length);
    }

    lastWord.text = lastText + String.fromCharCode(34) + punctuation;

    this.underSelection = false;

    this.onChange();

    return;
  }

  const hasHyphenWithSpace = text.startsWith('- ');
  const hasHyphen = !hasHyphenWithSpace && text.startsWith('-');

  if (hasHyphenWithSpace) {
    text = text.substring(2);
  } else if (hasHyphen) {
    text = text.substring(1);
  }

  if (
    text.startsWith(String.fromCharCode(34)) &&
    text.endsWith(String.fromCharCode(34))
  ) {
    return;
  }

  this.addNewActionForUndo('addQuotes');

  if (
    text.startsWith(String.fromCharCode(34)) ||
    text.endsWith(String.fromCharCode(34))
  ) {
    text = text.replace(String.fromCharCode(34), '');
  }

  let punctuation = '';
  if (hasPunctuation(text)) {
    text = removePunctuation(text);
    punctuation = this.currentWord.text.substring(text.length);
  }

  this.currentWord.text =
    String.fromCharCode(34) + text + String.fromCharCode(34) + punctuation;

  if (hasHyphenWithSpace) {
    this.currentWord.text = `- ${this.currentWord.text}`;
  } else if (hasHyphen) {
    this.currentWord.text = `-${this.currentWord.text}`;
  }

  this.onChange();
}

// ***************************************************************************************
// addApostrophes permet d'ajouter des apostrophes
// ***************************************************************************************
export function addApostrophes(one) {

  if (!this.currentWord) {
    
    if (!this.currentLine.words.length) {
      this.addNewActionForUndo('addPeriod');
      this.createNewWord(this.subtitle, String.fromCharCode(39));
      this.onChange();

      if (this.currentLetterIndex === 0){
        this.currentLetterIndex++;
      }
    } 
      
    return;
    
  }

  let { text } = this.currentWord;

  // On historise
  this.addNewActionForUndo('addApostrophes');

  // on ajoute les apostrophes au début et à la fin du mot
  if (this.underSelection) {
    const firstWord = first(this.wordsToSelect);
    const lastWord = last(this.wordsToSelect);

    firstWord.text = String.fromCharCode(39) + firstWord.text;

    let lastText = lastWord.text;
    let punctuation = '';
    if (hasPunctuation(lastText)) {
      lastText = removePunctuation(lastText);
      punctuation = lastWord.text.substring(lastText.length);
    }

    lastWord.text = lastText + String.fromCharCode(39) + punctuation;

    this.underSelection = false;

    this.onChange();

    return;
  }

  // on ajoute une apostrophe au mot
  if (one) {
    if (text.includes(String.fromCharCode(39))) {
      this.cancelUndo();
      return;
    }

    this.currentWord.text =
      text.substring(0, 1) + String.fromCharCode(39) + text.substring(1);

    this.currentLetterIndex++;

    this.onChange();

    return;
  }

  const hasHyphenWithSpace = text.startsWith('- ');
  const hasHyphen = !hasHyphenWithSpace && text.startsWith('-');

  if (hasHyphenWithSpace) {
    text = text.substring(2);
  } else if (hasHyphen) {
    text = text.substring(1);
  }

  let punctuation = '';
  if (hasPunctuation(text)) {
    text = removePunctuation(text);
    punctuation = this.currentWord.text.substring(text.length);
  }

  this.currentWord.text =
    String.fromCharCode(39) + text + String.fromCharCode(39) + punctuation;

  if (hasHyphenWithSpace) {
    this.currentWord.text = `- ${this.currentWord.text}`;
  } else if (hasHyphen) {
    this.currentWord.text = `-${this.currentWord.text}`;
  }

  this.onChange();
}

// ***************************************************************************************
// addOrRemoveLetter permet d'ajouter ou supprimer une lettre
// ***************************************************************************************
export function addOrRemoveLetter(key) {

  if (!this.currentWord) {
    return;    
  }

  let letter;
  let { text } = this.currentWord;

  let punctuation = '';
  text = removePunctuation(text);
  punctuation = this.currentWord.text.substring(text.length);

  switch (key) {
    case 'ADDORREMOVESLETTER':
      if (text.endsWith('eau') || text.endsWith('eaux')) {
        letter = 'x';
        break;
      }

      letter = 's';
      break;

    case 'ADDORREMOVENTLETTER':
      letter = 'nt';
      break;

    default:
      return;
  }

  // On historise
  this.addNewActionForUndo('addOrRemoveLetter');

  const hasDoubleQuotes = text.endsWith(String.fromCharCode(34));
  if (hasDoubleQuotes) {
    text = text.substring(0, text.length - 1);
  }

  const wordLetter = text.substring(text.length - letter.length);

  if (wordLetter === letter) {
    this.currentWord.text =
      text.substring(0, text.length - letter.length) + punctuation;
  } else {
    this.currentWord.text = text + letter + punctuation;
  }

  if (hasDoubleQuotes) {
    this.currentWord.text += String.fromCharCode(34);
  }

  this.currentLetterIndex = undefined;

  this.onChange();
}

// ***************************************************************************************
// invertPeriodAndComma permet d'inverser le point et la virgule
// ***************************************************************************************
export function invertPeriodAndComma() {
  if (!this.currentWord || !this.currentWord.letters.length) {
    return;
  }

  const { text, indexLine } = this.currentWord;

  const letter = text.substring(text.length - 1);

  if (letter !== '.' && letter !== ',') {
    return;
  }

  // On historise
  this.addNewActionForUndo('invertPeriodAndComma');

  let letterToChange = letter === '.' ? ',' : '.';

  this.currentWord.text = text.replace(letter, letterToChange);

  // On met au majuscule ou au minuscule le prochain mot qui n'est pas à la ligne
  const nextWord = this.getNextWord();
  if (nextWord && !nextWord.indexLine !== indexLine) {
    nextWord.text =
      letterToChange === '.'
        ? upperFirst(nextWord.text)
        : lowerFirst(nextWord.text);
  }

  this.onChange();
}

// ***************************************************************************************
// invertALetter permet d'inverser le a et à
// ***************************************************************************************
export function invertALetter() {

  if (!this.currentWord) {
    return;
  }

  const { text } = this.currentWord;

  if (text !== 'a' && text !== 'à') {
    return;
  }

  // On historise
  this.addNewActionForUndo('invertALetter');

  this.currentWord.text = text === 'a' ? 'à' : 'a';

  this.onChange();
}

// ***************************************************************************************
// hyphenateTwoWords permet de regrouper deux mots par un tiret
// charCode 45 : Trait d'union - Signe moins
// ***************************************************************************************
export function hyphenateTwoWords() {
  if (!this.underSelection) {
    this.groupWordByChar('hyphenateTwoWords', String.fromCharCode(45), true);
    return;
  }

  if (this.wordsToSelect.length > 2) {
    return;
  }

  // On historise
  this.addNewActionForUndo('hyphenateTwoWords');

  const firstWord = first(this.wordsToSelect);
  const lastWord = last(this.wordsToSelect);

  firstWord.text += String.fromCharCode(45) + lastWord.text;
  firstWord.out = lastWord.out;

  this.currentLine.words.splice(lastWord.index, 1);

  this.underSelection = false;
  this.currentWordIndex = firstWord.index;

  this.onChange();
}

// ***************************************************************************************
// replaceHyphenate permet de remplacer le tiret dans le mot par un espace ou un espace insécable
// ***************************************************************************************
export function replaceHyphenate(unsecableSpace) {
  if (!this.currentWord) {
    return;
  }

  const { text, index } = this.currentWord;

  if (!text.includes(String.fromCharCode(45))) {
    return;
  }

  this.addNewActionForUndo('replaceHyphenate');

  if (unsecableSpace) {
    this.currentWord.text = text.replace(
      String.fromCharCode(45),
      String.fromCharCode(0xa0)
    );
  } else {
    const texts = this.currentWord.text.split(String.fromCharCode(45));

    const newWords = this.createWords(texts);

    this.currentLine.words.splice(index, 1, ...newWords);
  }

  this.onChange();
}

// ***************************************************************************************
// addOrRemoveLetterWithApostrophe permet d'ajouter ou d'enlever la lettre avec apostrophe
// Les lettres concernées : c, d, j, l, m, n, qu, s, t
// ***************************************************************************************
export function addOrRemoveLetterWithApostrophe(key) {

  if (!this.currentWord) {
    return;
  }

  let { text } = this.currentWord;

  let letter = key[key.length - 1];

  if (letter === 'Q') {
    letter = 'qu';
  }

  const letterWithApostrophe = letter.toLowerCase() + String.fromCharCode(39);

  this.addNewActionForUndo('addOrRemoveLetterWithApostrophe');

  const hasHyphenWithSpace = text.startsWith('- ');
  const hasHyphen = !hasHyphenWithSpace && text.startsWith('-');

  if (hasHyphenWithSpace) {
    text = text.substring(2);
  } else if (hasHyphen) {
    text = text.substring(1);
  }

  const hasDoubleQuotes = text.startsWith(String.fromCharCode(34));
  if (hasDoubleQuotes) {
    text = text.substring(1);
  }

  if (text.toLowerCase().startsWith(letterWithApostrophe)) {
    this.currentWord.text = text.substring(letterWithApostrophe.length);
  } else {
    this.currentWord.text = letterWithApostrophe + text.toLowerCase();
  }

  if (hasDoubleQuotes) {
    this.currentWord.text = String.fromCharCode(34) + this.currentWord.text;
  }

  if (hasHyphenWithSpace) {
    this.currentWord.text = `- ${upperFirst(this.currentWord.text)}`;
  } else if (hasHyphen) {
    this.currentWord.text = `-${upperFirst(this.currentWord.text)}`;
  }

  const prevWord = this.getPrevWord();
  if (prevWord && prevWord.text.includes('.')) {
    this.currentWord.text = upperFirst(this.currentWord.text);
  }

  this.onChange();
}

// ***************************************************************************************
// addOrRemoveLetterWithApostrophe permet d'ajouter ou d'enlever l'apostrophe sur la lettre
// Les lettres concernées : c, d, j, l, m, n, qu, s, t
// ***************************************************************************************
export function addOrRemoveApostropheToLetter(key) {

  if (!this.currentWord) {
    return;
  }

  let { text } = this.currentWord;

  let letter = key[key.length - 1].toLowerCase();

  if (letter === 'q') {
    letter = 'qu';
  }

  const hasDoubleQuotes = text.startsWith(String.fromCharCode(34));
  if (hasDoubleQuotes) {
    text = text.substring(1);
  }

  if (!text.toLowerCase().startsWith(letter)) {
    return;
  }

  this.addNewActionForUndo('addOrRemoveApostropheToLetter');

  const letterWithApostrophe = letter.toLowerCase() + String.fromCharCode(39);

  if (text.toLowerCase().includes(String.fromCharCode(39))) {
    this.currentWord.text = letter + text.substring(letter.length + 1);
  } else {
    this.currentWord.text =
      letterWithApostrophe + text.substring(letter.length);
  }

  if (hasDoubleQuotes) {
    this.currentWord.text = String.fromCharCode(34) + this.currentWord.text;
  }

  const prevWord = this.getPrevWord();
  if (prevWord && prevWord.text.includes('.')) {
    this.currentWord.text = upperFirst(this.currentWord.text);
  }

  this.onChange();
}

// ***************************************************************************************
// replaceLetter permet de remplacer les lettres
// ***************************************************************************************
export function replaceLetter(key) {
  if (!this.currentWord) {
    return;
  }

  let letters = [];

  switch (key) {
    case 'REPLACE_E':
      letters = [
        {
          containsLetter: [
            'ées',
            'ée',
            'eais',
            'eait',
            'eaient',
            'ais',
            'ait',
            'aient',
            'er',
            'ez',
          ],
          replaceLetter: 'é',
        },
        {
          containsLetter: ['é'],
          replaceLetter: 'er',
        },
      ];
      break;
    case 'REPLACE_R':
      letters = [
        {
          containsLetter: [
            'eais',
            'eait',
            'eaient',
            'ais',
            'ait',
            'aient',
            'é',
            'ez',
            'ai',
          ],
          replaceLetter: 'er',
        },
        {
          containsLetter: ['er'],
          replaceLetter: 'ez',
        },
      ];
      break;
    case 'REPLACE_Z':
      letters = [
        {
          containsLetter: ['r'],
          replaceLetter: 'z',
        },
        {
          containsLetter: [
            'eais',
            'eait',
            'eaient',
            'ais',
            'ait',
            'aient',
            'é',
            'ai',
          ],
          replaceLetter: 'ez',
        },
        {
          containsLetter: ['z'],
          replaceLetter: 'r',
        },
      ];
      break;

    case 'REPLACE_T':
      letters = [
        {
          containsLetter: ['é', 'ait'],
          replaceLetter: 'aient',
        },
        {
          containsLetter: ['aient'],
          replaceLetter: 'ait',
        },
      ];
      break;

    default:
      return;
  }

  let { text } = this.currentWord;

  let punctuation = '';
  if (hasPunctuation(text)) {
    text = removePunctuation(text);
    punctuation = this.currentWord.text.substring(text.length);
  }

  let lettersToDelete = '';
  let letterToReplace = '';
  for (const letter of letters) {
    const element = letter.containsLetter.find((suffixe) =>
      text.endsWith(suffixe)
    );
    if (element) {
      lettersToDelete = element;
      letterToReplace = letter.replaceLetter;
      break;
    }
  }

  if (!lettersToDelete && !letterToReplace) {
    return;
  }

  this.addNewActionForUndo('replaceLetter');

  this.currentWord.text =
    text.substring(0, text.length - lettersToDelete.length) +
    letterToReplace +
    punctuation;

  this.onChange();
}

// ***************************************************************************************
// addOrRemoveLeToWord permet d'ajouter 'le' ou de l'enlever
// ***************************************************************************************
export function addOrRemoveLeToWord() {
  if(!this.currentWord) {
    return;
  }

  let { text } = this.currentWord;

  this.addNewActionForUndo('addOrRemoveLeToWord');

  let punctuation = '';
  if (hasPunctuation(text)) {
    text = removePunctuation(text);
    punctuation = this.currentWord.text.substring(text.length);
  }

  if (text.endsWith('le')) {
    this.currentWord.text = text.substring(0, text.length - 2) + punctuation;
  } else {
    this.currentWord.text = text + 'le' + punctuation;
  }

  this.onChange();
}
